/**
 * W3C Trace Context helper (TASK-079 Phase 2 / FEAT-058).
 *
 * `getActiveTraceparent()` reads the currently active Sentry span (provided by
 * Sentry's BrowserTracing integration) and renders a W3C-compliant `traceparent`
 * header value: `00-{32hex traceId}-{16hex spanId}-{2hex flags}`.
 *
 * Injection point: `frontend/src/api/client.js` adds the header on every
 * outbound request so backend Lambdas can correlate CloudWatch logs with the
 * user-side action that triggered them.
 *
 * In dev/staging (tracesSampleRate=1.0) flags is "01" (sampled). In prod, for
 * unsampled requests Sentry returns a traceparent with flags "00" so the
 * backend still sees the traceId without committing to retain the trace.
 *
 * Returns null when:
 *   - Sentry is not initialized (e.g. dev with no DSN) — no active span
 *   - The span lacks traceId or spanId
 *   - The span context is null (defensive)
 *
 * The caller (apiClient) skips the header when this returns null; the fetch
 * still succeeds.
 *
 * Spec: memory-bank/creative/TASK-079-phase2-observability.md
 *       (`traceparent` Injection Recipe section)
 */

import * as Sentry from '@sentry/react';

export const TRACEPARENT_HEADER = 'traceparent';

export function getActiveTraceparent() {
  const span = Sentry.getActiveSpan();
  if (!span) return null;
  const ctx = span.spanContext();
  if (!ctx) return null;
  if (!ctx.traceId || !ctx.spanId) return null;
  const flags = (ctx.traceFlags & 0x01) ? '01' : '00';
  return `00-${ctx.traceId}-${ctx.spanId}-${flags}`;
}
